import { useCallback } from 'react';
import { useSignIn } from '@clerk/nextjs';
import { isClerkAPIResponseError, isClerkError } from '@clerk/shared/error';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

import { prepareFirstFactor } from '../factors';
import type { SignInStrategy } from '../type';

const schema = z.object({
  email: z.email().trim(),
});

type Props = Readonly<{
  /**
   * The verification strategy to use.
   *
   * @default 'password'
   */
  verification?: SignInStrategy;
}>;

export const EmailStep = ({ verification = 'password' }: Props) => {
  const { signIn, fetchStatus } = useSignIn();

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
    },
  });

  const handleSubmit = useCallback(
    async ({ email }: z.infer<typeof schema>) => {
      // Start the sign-in process using the email
      const { error } = await signIn.create({
        identifier: email,
      });
      if (error) {
        if (isClerkAPIResponseError(error)) {
          form.setError('email', { type: 'manual', message: error.errors[0].longMessage });
        } else if (isClerkError(error)) {
          form.setError('email', { type: 'manual', message: error.longMessage });
        }
        console.error(JSON.stringify(error, null, 2));
        return;
      }

      await prepareFirstFactor({ strategy: verification, signIn });
    },
    [form, signIn, verification],
  );

  return (
    <Card className="w-full min-w-96">
      <CardHeader>
        <CardTitle>Sign in to iSeeWhere</CardTitle>
        <CardDescription>Welcome back! Please sign in to continue</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-y-4">
        <form id="email-form" onSubmit={form.handleSubmit(handleSubmit)}>
          <FieldGroup>
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.name}>Email address</FieldLabel>
                  <Input
                    {...field}
                    id={field.name}
                    inputMode="email"
                    aria-invalid={fieldState.invalid}
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck="false"
                    autoFocus
                  />
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
          </FieldGroup>
        </form>
      </CardContent>
      <CardFooter>
        <Field orientation="horizontal">
          <Button type="submit" form="email-form" className="w-full" disabled={fetchStatus === 'fetching'}>
            Continue
          </Button>
        </Field>
      </CardFooter>
    </Card>
  );
};
